/**
 * Institutions Service Types
 *
 * Types for managing financial institutions and organizations
 */

import { z } from 'zod';

// ============================================================================
// Core Types
// ============================================================================

/**
 * Institution stage for tracking progress
 */
export interface InstitutionStage {
  stage: 'discovery' | 'representation' | 'collection';
  completionState: 'complete' | 'in_progress' | 'not_started';
  statusDetail?: string;
}

/**
 * Institution entity
 */
export interface Institution {
  id: number;
  name: string;
  type: string;
  abbreviation?: string;
  website?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  notes?: string;
  faviconUrl?: string | null;
  isCustom?: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;

  // Relations
  institutionTypeId?: number;
  institutionType?: InstitutionType;

  // Progress tracking (returned from GET /matters/{matterId}/institutions)
  stages?: InstitutionStage[];

  /**
   * Distance from the proximity anchor, in kilometres, for a picker row (VALE-1596).
   *
   * Set by `GET /institutions` ONLY in proximity mode (a distance-ranked search or the
   * no-query nearest browse); a non-proximity listing carries none. `null` on a
   * coordinate-less row that a proximity search still returns (ranked last), so a reader
   * sees nothing rather than "0 km". The backend recomputes it in TS from the same
   * coordinates the ranking used, so the shown distance and the order cannot drift.
   */
  distanceKm?: number | null;
}

/**
 * Institution type entity
 */
export interface InstitutionType {
  id: number;
  name: string;
  slug: string;
  description?: string;
  sortOrder: number;
  isActive: boolean;
}

/**
 * Matter institution relation
 */
export interface MatterInstitution {
  id: number;
  matterId: number;
  institutionId: number;
  institution: Institution;
  isPrimary: boolean;
  notes?: string;
  /**
   * The `MatterInstitution` relationship-row PK — the id the collection (payout)
   * endpoint is keyed on: `POST /matters/:matterId/matter-institutions/
   * :matterInstitutionId/payouts` (LF-4022). Distinct from the global
   * `institutionId`; the two id-spaces can collide in value, so always resolve
   * by matching the global `institutionId` and reading this field — never by
   * value-equality across the two spaces.
   */
  matterInstitutionId?: number | null;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Request/Response Types
// ============================================================================

/**
 * One entry of `GET /institution-types`, as the backend actually returns it.
 *
 * The registry (`backend/src/institutions/constants/institution-types.constants.ts`)
 * is the source of truth for the set and for `inventoryItemTypes` — which items a
 * type holds. Read them from here rather than restating either: a type added to the
 * registry reaches every consumer of this endpoint with no further change
 * (VALE-1577).
 */
export interface InstitutionTypeOption {
  slug: string;
  name: string;
  logoUrl: string | null;
  isNotifiable: boolean;
  notificationLetterSubject: string;
  correspondenceLetterBody: string;
  /** The inventory item types this institution type holds. */
  inventoryItemTypes: Array<{
    id: string;
    name: string;
    slug: string;
    iconUrl?: string;
  }>;
}

/**
 * Institution types list response — a **bare array**, not an envelope.
 *
 * `InstitutionTypesService.getInstitutionTypes()` returns `Object.values(...)`
 * sorted by name. This was previously declared as `{ types, total }`, a shape the
 * endpoint has never returned; nothing consumed the hook, so the mismatch was
 * invisible until the create step needed the list (VALE-1577).
 */
export type InstitutionTypesListResponse = InstitutionTypeOption[];

/**
 * Matter institutions list response
 */
export interface MatterInstitutionsListResponse {
  institutions: MatterInstitution[];
  total: number;
}

// ============================================================================
// Batch add — Discovery drawer "Add N" (POST /matters/:matterId/institutions/batch)
// ============================================================================

/** Per-institution outcome of a batch add. */
export type BatchAddInstitutionOutcome = 'created' | 'restored' | 'skipped';

export interface BatchAddInstitutionResult {
  institutionId: number;
  outcome: BatchAddInstitutionOutcome;
}

export interface BatchCreateMatterInstitutionsResponse {
  /** One entry per requested institution id, in request order. */
  results: BatchAddInstitutionResult[];
}

// ============================================================================
// Grouped institutions — Discovery drawer popular tiles + search
// (GET /institutions/grouped). Notifiable global institutions grouped by type,
// popular-first then alphabetical.
// ============================================================================

export interface GroupedInstitutionItem {
  id: number;
  name: string;
  /** Institution type slug. */
  type: string;
  faviconUrl: string | null;
  /** Curated quick-add (popular) institution. */
  isPopular: boolean;
  /** Ordering within this type's popular tiles (lower first); null when not popular. */
  displayOrder: number | null;
}

export interface GroupedInstitutionTypeInfo {
  slug: string;
  name: string;
}

export interface GroupedInstitutionsByType {
  type: GroupedInstitutionTypeInfo;
  institutions: GroupedInstitutionItem[];
}

/**
 * Institution filters
 */
export interface InstitutionFilters {
  type?: string;
  inventoryItemType?: string;
  q?: string; // Search query
  search?: string; // Legacy - keep for compatibility
  isActive?: boolean;
  /** See `InstitutionFiltersSchema.sort` — only meaningful when `q` is absent. */
  sort?: 'name' | 'popular';
  page?: number;
  limit?: number; // Backend expects 'limit' not 'pageSize'
}

/**
 * Input for updating a custom institution
 */
export interface UpdateInstitutionInput {
  name?: string;
  email?: string;
}

// ============================================================================
// Validation Schemas
// ============================================================================

export const InstitutionFiltersSchema = z.object({
  type: z.string().optional(),
  inventoryItemType: z.string().optional(),
  q: z.string().optional(),
  search: z.string().optional(),
  isActive: z.boolean().optional(),
  /**
   * Ordering for a listing with no `q` (VALE-1031). `popular` puts the curated
   * shortlist first — the same `is_popular` / `display_order` columns the Discovery
   * drawer's popular tiles run on — then falls through to alphabetical. Ignored by
   * the backend when `q` is set.
   */
  sort: z.enum(['name', 'popular']).optional(),
  page: z.number().int().positive().optional(),
  limit: z.number().int().positive().max(100).optional()
});

// ============================================================================
// Action Result Types
// ============================================================================

export interface InstitutionActionResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  validationErrors?: Record<string, string[]>;
}

// ============================================================================
// Common Institution Types
// ============================================================================

export const COMMON_INSTITUTION_TYPES = [
  'bank',
  'superannuation',
  'insurance',
  'investment',
  'government',
  'utility',
  'telecommunications',
  'healthcare',
  'legal',
  'other'
] as const;

// ============================================================================
// Letter types — what the firm is ASKING a party (VALE-1163)
// ============================================================================

/**
 * One choice in the "what are you asking them?" picker.
 *
 * Vale used to choose a discovery letter from the KIND OF ORGANISATION being
 * written to, which works for catalogued financial institutions and breaks for
 * everything else: a former employer, a trade creditor and an accountant all
 * received the same generic "any assets, accounts or deposits" letter written
 * for a bank. The enquiry type is the second axis that fixes that.
 *
 * Served from `GET /institutions/letter-types` rather than hard-coded here, so
 * the label a solicitor reads and the template the backend renders can never be
 * two different definitions of the same letter type.
 */
export interface LetterType {
  slug: string;
  label: string;
  description: string;
  /**
   * The contact relationships this letter is the obvious answer for — what the picker opens on for
   * a contact recorded as the accountant, the former solicitor or a former employer.
   *
   * Optional because a response served before this field existed carries none, and an absent list
   * degrades to the picker's own default rather than to an error.
   */
  defaultForRelationships?: string[];
}

/** The default a newly created party takes: the generic assets enquiry. */
export const DEFAULT_LETTER_TYPE_SLUG = 'assets-held';

/** Request body for `POST /matters/:matterId/institutions/party`. */
export interface CreateMatterPartyRequest {
  name: string;
  email?: string;
  letterType: string;
}

/** Request body for `POST /matters/:matterId/contacts` when adding a letter recipient. */
export interface CreateMatterContactRecipientRequest {
  firstName: string;
  lastName: string;
  email?: string;
  letterType: string;
  isLegalPersonalRepresentative: false;
  isBeneficiary: false;
  isClient: false;
}

/**
 * Making a contact already on the matter a letter recipient.
 *
 * Carries the letter type and nothing else: every other fact about the person
 * is already recorded, and this request must not be able to overwrite any of
 * it. `contactId` addresses the row; it is not part of the body.
 */
export interface AttachMatterContactRecipientRequest {
  contactId: number;
  letterType: string;
}

/**
 * An institution the firm can already see whose name is the same place as the one the
 * reader is creating (VALE-1422). Returned by
 * `GET /institutions/duplicate-candidates`.
 *
 * Every field is here to answer "is this the place I meant?" without the reader having
 * to remember what they did last week — which is the failure this guard addresses: a
 * firm created the same aged-care facility on consecutive days, on different matters,
 * one hyphen apart.
 */
export interface InstitutionDuplicateCandidate {
  institutionId: number;
  name: string;
  /**
   * The type registry's human label (e.g. `Bank`), not a slug (VALE-1585). The
   * matcher is deliberately type-blind, so a candidate can be a different type
   * from the field the reader is creating from.
   */
  type: string;
  email: string | null;
  /** A Vale catalogue row (true) or one this firm added (false). */
  isGlobal: boolean;
  /** Set only for a firm-owned row, so the panel can say whose it is. */
  lawFirmName: string | null;
  /** How many matters already use it. 0 reads as "added but never used". */
  matterCount: number;
  /** Active institution forms Vale holds — a reason to prefer the catalogue row. */
  formCount: number;
  createdAt: string;
}

/**
 * The retired row standing in the way of a create, as described by a 409
 * `ArchivedInstitution` (VALE-1497).
 *
 * Raised only when nothing is known to have superseded it. Where the promotion queue
 * recorded a successor the create simply returns that institution instead, with no
 * question asked — so a reader who sees this panel is genuinely the only one who can
 * say whether they meant one of the live look-alikes or want their own row back.
 */
export interface ArchivedInstitutionSummary {
  institutionId: number;
  name: string;
  /** The type registry's human label (e.g. `Bank`), not a slug (VALE-1585). */
  type: string;
  /** When it was retired. The one fact that explains why it is not in the picker. */
  retiredAt: string;
  /** Matters that still reference it. 0 reads as "added but never used". */
  matterCount: number;
}

/** The body of a 409 `ArchivedInstitution` from `POST /institutions`. */
export interface ArchivedInstitutionErrorBody {
  error: 'ArchivedInstitution';
  message: string;
  archived: ArchivedInstitutionSummary;
  candidates: InstitutionDuplicateCandidate[];
}
